import Dexie from "dexie";
import api from "@/lib/api";

// Local database on the phone (IndexedDB) for offline working
export const dexdb = new Dexie("anila_offline");
dexdb.version(1).stores({
  consumers: "consumer_no",      // cached master list for offline validation
  outbox: "++id, kind, created_at", // queued actions made while offline
  meta: "key",
});

export const isOnline = () => (typeof navigator !== "undefined" ? navigator.onLine : true);

// ---- Consumer cache (for offline validation) ----
export async function refreshConsumerCache() {
  if (!isOnline()) return;
  try {
    // pull a large page of consumers (numbers + basic info) for local validation
    const r = await api.get("/consumers?limit=200&skip=0");
    // The list endpoint is capped at 200; for the pilot we cache progressively as drivers search.
    // Also cache the full set via a dedicated lightweight call if available.
    await dexdb.consumers.bulkPut((r.data.items || []).map((c) => ({
      consumer_no: c.consumer_no, name: c.name, area: c.area, package_desc: c.package_desc,
    })));
    await dexdb.meta.put({ key: "consumers_synced_at", value: new Date().toISOString() });
  } catch (e) { /* ignore */ }
}

// Cache all consumers in chunks (called once after driver login when online)
export async function primeConsumerCache() {
  if (!isOnline()) return;
  try {
    const have = await dexdb.consumers.count();
    const meta = await dexdb.meta.get("consumers_synced_at");
    const fresh = meta && (Date.now() - new Date(meta.value).getTime()) < 24 * 3600 * 1000;
    if (have > 100 && fresh) return; // already cached recently
    const stats = await api.get("/consumers/stats");
    const total = stats.data.active || 0;
    let skip = 0; const limit = 200;
    while (skip < total && skip < 6000) {
      const r = await api.get(`/consumers?limit=${limit}&skip=${skip}`);
      const items = r.data.items || [];
      if (items.length === 0) break;
      await dexdb.consumers.bulkPut(items.map((c) => ({
        consumer_no: c.consumer_no, name: c.name, area: c.area, package_desc: c.package_desc,
      })));
      skip += limit;
    }
    await dexdb.meta.put({ key: "consumers_synced_at", value: new Date().toISOString() });
  } catch (e) { /* ignore */ }
}

export async function validateConsumer(consumerNo) {
  const no = String(consumerNo).trim();
  if (isOnline()) {
    try {
      const r = await api.get(`/consumers/validate/${no}`);
      if (r.data.valid) await dexdb.consumers.put({
        consumer_no: r.data.consumer.consumer_no, name: r.data.consumer.name,
        area: r.data.consumer.area, package_desc: r.data.consumer.package_desc,
      });
      return { ...r.data, offline: false };
    } catch (e) { /* fall through to local */ }
  }
  const local = await dexdb.consumers.get(no);
  return { valid: !!local, consumer: local || null, offline: true };
}

// ---- Outbox (queued actions) ----
export async function pendingCount() {
  return await dexdb.outbox.count();
}

async function postOrQueue(kind, path, body) {
  if (isOnline()) {
    try {
      const r = await api.post(path, body);
      return { ok: true, queued: false, data: r.data };
    } catch (e) {
      if (e?.response) throw e; // real server error (e.g. validation) — surface it
      // network error -> queue
    }
  }
  await dexdb.outbox.add({ kind, path, body, created_at: new Date().toISOString() });
  return { ok: true, queued: true };
}

export const submitLoad = (body) => postOrQueue("load", "/loads", body);
export const submitTransaction = (body) => postOrQueue("transaction", "/transactions", body);
export const submitReturn = (body) => postOrQueue("return", "/returns", body);

export async function flushOutbox() {
  if (!isOnline()) return { sent: 0, remaining: await pendingCount() };
  const items = await dexdb.outbox.orderBy("id").toArray();
  let sent = 0;
  for (const it of items) {
    try {
      await api.post(it.path, it.body);
      await dexdb.outbox.delete(it.id);
      sent += 1;
    } catch (e) {
      if (e?.response && e.response.status >= 400 && e.response.status < 500) {
        // unrecoverable (validation) — drop so it doesn't block the queue forever
        await dexdb.outbox.delete(it.id);
      } else {
        break; // network issue — stop, retry later
      }
    }
  }
  return { sent, remaining: await pendingCount() };
}
